import React, { FC, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { format, isToday } from 'date-fns'

// COMPONENTS
import * as Icons from '@icons'
import Card from '@components/atoms/card'
import CollapsibleElement from '@components/atoms/collapsable-element'
import Divider from '@/components/atoms/divider'
import './live-activity-feed.css'



type GroupStatus = 'active' | 'pending' | 'completed' | 'finalized' | 'canceled' | string

interface Props {
    activities: Activity[]
    groupStatus: GroupStatus
    open?: boolean
    onOpenChange?: (open: boolean) => void
}

const LiveActivityFeed: FC<Props> = ({ activities, groupStatus, open = false, onOpenChange }) => {
    const [internalOpen, setInternalOpen] = useState(open)
    const { user } = useUser()
    const isControlled = typeof onOpenChange === 'function'
    const isOpen = isControlled ? open : internalOpen

    function setIsOpen(nextState: React.SetStateAction<boolean>) {
        const nextValue = typeof nextState === 'function'
            ? nextState(isControlled ? open : internalOpen)
            : nextState

        if (!isControlled) setInternalOpen(nextValue)
        onOpenChange?.(nextValue)
    }

    const getActivityIcon = (type: Activity['Type']) => {
        switch (type) {
            case 'card_transaction':
                return (
                    <div className="bubble-xxsml flex items-center justify-center !bg-yellow-500">
                        <Icons.CreditCard type="simple" tint="var(--splitcard-white)" />
                    </div>
                )
            case 'additional_funds_add':
                return (
                    <div className="bubble-xxsml flex items-center justify-center !bg-green-600">
                        <Icons.Coins tint="var(--splitcard-white)" />
                    </div>
                )
            case 'user_joined':
                return (
                    <div className="bubble-xxsml flex items-center justify-center !bg-blue-500">
                        <Icons.AddUser tint="var(--splitcard-white)" />
                    </div>
                )
            case 'user_left':
                return (
                    <div className="bubble-xxsml flex items-center justify-center !bg-red-600">
                        <Icons.RemoveUser tint="var(--splitcard-white)" />
                    </div>
                )
            case 'group_created':
                return (
                    <div className="bubble-xxsml flex items-center justify-center !bg-blue-600">
                        <Icons.Create tint="var(--splitcard-white)" />
                    </div>
                )
            case 'group_goal_updated':
                return (
                    <div className="bubble-xxsml flex items-center justify-center !bg-green-700">
                        <Icons.Update tint="var(--splitcard-white)" />
                    </div>
                )
            default:
                return (
                    <div className="bubble-xxsml flex items-center justify-center">
                    </div>
                )
        }
    }

    if (!activities || activities.length === 0) return null

    return (
        <CollapsibleElement state={[isOpen, setIsOpen]}>
            <div className="flex flex-row items-center justify-between live-activity-trigger">
                <div className="flex flex-row items-center gap-2">
                    <div className={`live-indicator ${(groupStatus === 'active' || groupStatus === 'pending' || groupStatus === 'completed') ? 'active' : ''}`} />
                    <p className="text-body text-gray-500"><strong>{`${(groupStatus === 'active' || groupStatus === 'pending' || groupStatus === 'completed') ? 'LIVE' : 'GROUP'} ACTIVITY`}</strong></p>
                </div>
                <div className="bubble-xxxsml flex items-center justify-center">
                    <Icons.Chevron width="0.75rem" height="0.75rem" facing={isOpen ? 'down' : 'up'} tint="var(--splitcard-blue-500)" />
                </div>
            </div>
            <div className="live-activity-content">
                <Card>
                    <div className="activity-list">
                        {activities.map((item, index) => (
                            <React.Fragment key={index}>
                                <div className="activity-item">
                                    {getActivityIcon(item.Type)}
                                    <div className="flex flex-col">
                                        <p className="text-body">{item.Message.replace(user?.fullName || '', 'You')}</p>
                                        <p className="text-body-2 text-gray-500">{isToday(item.Timestamp) ? `Today, ${format(item.Timestamp, 'p')}` : format(item.Timestamp, 'MM/dd/yy, p')}</p>
                                    </div>
                                </div>
                                {index < activities.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </div>
                </Card>
            </div>
        </CollapsibleElement>
    )
}

export default LiveActivityFeed
